import { useQuery } from '@tanstack/react-query';
import { DateTime } from 'luxon';
import type { LeavePolicyDto, LeaveRequestDto, LeaveTypeDto } from '@workforce/shared';
import { api } from './api';
import { t } from './i18n';

export const useLeaveTypes = (enabled = true) =>
  useQuery({ queryKey: ['leave-types'], queryFn: () => api.get<LeaveTypeDto[]>('/leave-types'), enabled });
export const useMyLeaveTypes = (enabled = true) =>
  useQuery({ queryKey: ['me', 'leave-types'], queryFn: () => api.get<LeaveTypeDto[]>('/me/leave/types'), enabled });
export const useLeavePolicies = (enabled = true) =>
  useQuery({ queryKey: ['leave-policies'], queryFn: () => api.get<LeavePolicyDto[]>('/leave-policies'), enabled });

export function fmtDays(n: number): string {
  const v = Math.round(n * 100) / 100;
  return v === 1 ? t('{n} day', { n: v }) : t('{n} days', { n: v });
}

export function fmtRange(r: Pick<LeaveRequestDto, 'startDate' | 'endDate' | 'startHalf' | 'endHalf'>): string {
  const s = DateTime.fromISO(r.startDate);
  const e = DateTime.fromISO(r.endDate);
  const half = (h: boolean, label: string) => (h ? ` (${label})` : '');
  if (r.startDate === r.endDate) {
    return `${s.toFormat('ccc dd LLL yyyy')}${r.startHalf ? ` (${t('afternoon')})` : r.endHalf ? ` (${t('morning')})` : ''}`;
  }
  const sameYear = s.year === e.year;
  return t('{start} → {end}', {
    start: `${s.toFormat(sameYear ? 'dd LLL' : 'dd LLL yyyy')}${half(r.startHalf, t('pm'))}`,
    end: `${e.toFormat('dd LLL yyyy')}${half(r.endHalf, t('am'))}`,
  });
}

export const statusTone = (s: string) =>
  (({ pending: 'amber', approved: 'green', rejected: 'red', cancelled: 'slate' }) as const)[s as 'pending'] ?? 'slate';

/** Plain-language rules of a leave type (for the request form, balances and setup). */
export function ruleLines(
  x: {
    requiresDocument: boolean;
    maxConsecutiveDays: number | null;
    minGapDays: number | null;
    minServiceMonths: number | null;
    maxTimesPerCareer: number | null;
    maxDaysPerYear: number | null;
    countsCalendarDays: boolean;
  },
  drawsFromName?: string | null,
): string[] {
  const out: string[] = [];
  if (x.requiresDocument) out.push(t('Medical report required'));
  if (x.maxDaysPerYear) out.push(t('Up to {days} a year', { days: fmtDays(x.maxDaysPerYear) }));
  if (x.maxConsecutiveDays) out.push(t('At most {days} in a row', { days: fmtDays(x.maxConsecutiveDays) }));
  if (x.minGapDays) out.push(t('At least {n} days between two requests', { n: x.minGapDays }));
  if (x.minServiceMonths) out.push(t('After {n} months of service', { n: x.minServiceMonths }));
  if (x.maxTimesPerCareer) out.push(x.maxTimesPerCareer === 1 ? t('Once during the employment') : t('Up to {n} times during the employment', { n: x.maxTimesPerCareer }));
  if (x.countsCalendarDays) out.push(t('Calendar days (weekends included)'));
  if (drawsFromName) out.push(t('Taken from the {name} balance', { name: t(drawsFromName) }));
  return out;
}

/** Link that opens a stored file (medical report, policy PDF) in a new tab. */
export const fileUrl = (id: string, download = false) => `/api/files/${id}${download ? '?download=1' : ''}`;
